import React, { useState } from 'react';
import lodashCompact from 'lodash/fp/compact';

import { DATA_TABLE_SOURCE_UTILITY_PARTNERS } from 'common/dataTableConstants';
import { UTILITY_PARTNER_CUSTOMER_TYPE_LABELS } from 'common/utility/constants';
import formatUtilityPartnerId from 'common/utility/formatUtilityPartnerId';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrLink from 'client/components/SrLink/SrLink';
import SrDataTableUI from 'client/components/SrDataTableUI';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDataTableConnected from 'client/components/SrDataTableConnected';

const UPDataTablePartners = () => {
  const [searchInput, setSearchInput] = useState('');

  // Admin-only view: surface the canonical backend partner ID (`utilityPartners.id`)
  // alongside the existing identifying info so admins can distinguish partners with
  // similar names and cross-reference the exact record (FEAT-E72).
  const dataTableHeaders = [{ content: 'ID' }, { content: 'Name' }, { content: 'Link' }];

  return (
    <SrDataTableConnected
      dataSource={DATA_TABLE_SOURCE_UTILITY_PARTNERS}
      rootViewId={DATA_TABLE_SOURCE_UTILITY_PARTNERS}
      initialViewSettings={{
        sort: {
          createdAt: 'desc',
        },
      }}
      count={8}
      itemsPropName="partners"
      resetOnUnmount
    >
      {({ partners, viewSettings, dataTableApiState, loadNextPage, dataTableIsFullyLoaded }) => {
        const onSearchInputChange = (e) => {
          const searchTerm = e?.target?.value || '';
          setSearchInput(searchTerm);
          if (viewSettings) {
            viewSettings.update({
              ...viewSettings,
              loadAllData: true,
              fuzzy: !searchTerm
                ? null
                : {
                    queryString: searchTerm,
                    columns: ['name'],
                  },
            });
            loadNextPage();
          }
        };

        const partnerRows = partners.map((partner) => {
          const customerTypeLabel = UTILITY_PARTNER_CUSTOMER_TYPE_LABELS[partner.customerType];
          return {
            cells: [
              {
                content: (
                  <SrText
                    font="greycliff"
                    size="16"
                    weight="500"
                    lineHeight="50px"
                    wordBreak="break-word"
                  >
                    {formatUtilityPartnerId(partner.id)}
                  </SrText>
                ),
              },
              {
                content: lodashCompact([
                  partner.name,
                  customerTypeLabel,
                  `(${partner.status})`,
                ]).join(' - '),
              },
              {
                content: (
                  <SrLink
                    to={
                      `/utility-profit/${partner.slug}` +
                      `?backUrl=${global.encodeURIComponent('/utility-profit/admin')}`
                    }
                  >
                    <SrText
                      font="greycliff"
                      size="16"
                      weight="700"
                      lineHeight="50px"
                      color="blue300"
                    >
                      {`/utility-profit/${partner.slug}`}
                    </SrText>
                  </SrLink>
                ),
              },
            ],
          };
        });

        const loadMoreRow =
          dataTableIsFullyLoaded || viewSettings?.fuzzy?.queryString
            ? null
            : {
                onClick: () => (dataTableApiState.inProgress ? null : loadNextPage()),
                cells: [
                  {
                    content: (
                      <div
                        style={{ height: '100%', textAlign: 'center', backgroundColor: '#F5F9FF' }}
                      >
                        <SrText
                          lineHeight="50px"
                          font="greycliff"
                          weight="800"
                          size="16"
                          color="blue300"
                        >
                          View more partners
                        </SrText>
                      </div>
                    ),
                    colSpan: dataTableHeaders.length,
                    noPadding: true,
                  },
                ],
              };

        return (
          <SrBox>
            <SrBox marginTop="20px">
              <SrBox width="100%" height="60px">
                <SrFlex justify="between" align="baseline" columnGap="20px">
                  <SrText font="greycliff" size="28" lineHeight="34px" weight="700">
                    Utility Partners
                  </SrText>
                  <SrBox width="300px" height="40px">
                    <input
                      value={searchInput}
                      type="text"
                      onChange={onSearchInputChange}
                      placeholder="Search Partner"
                      style={{
                        height: '32px',
                        padding: '0 10px',
                        borderRadius: '8px',
                        border: '1px solid #E5E5E5',
                        fontFamily: 'greycliff',
                        fontSize: '13px',
                      }}
                    />
                  </SrBox>
                </SrFlex>
              </SrBox>

              <SrDataTableUI
                sticky
                theme={SR_TABLE_THEME_PARTNER_CLEAR}
                dataTableViewSettings={viewSettings}
                headers={dataTableHeaders}
                minWidth="100%"
                rows={lodashCompact([...partnerRows, loadMoreRow])}
              />
              {dataTableApiState.inProgress && (
                <div>
                  <div className="loading-ellipses clearfix" />
                </div>
              )}
            </SrBox>
            {partners.length === 0 && dataTableIsFullyLoaded && (
              <SrBox backgroundColor="white" display="flex" justifyContent="center" padding="20px">
                <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                  No partners added yet.
                </SrText>
              </SrBox>
            )}
          </SrBox>
        );
      }}
    </SrDataTableConnected>
  );
};

UPDataTablePartners.displayName = 'UPDataTablePartners';

export default UPDataTablePartners;
